<template>
    <el-popover ref="popover"
                @show="fetch"
                @hide="unsubscribe">
        <!-- Trigger -->
        <template slot="reference">
            <el-button type="text">
                <i class="el-icon-more"
                   style="transform: rotate(90deg)" />
            </el-button>
        </template>

        <!-- Content -->
        <div v-loading="loading"
             :class="classes.content">
            <!-- Templates -->
            <div v-if="hasTemplates"
                 class="space-y-1">
                <!-- Title -->
                <our-label :value="labels.documents"
                           primary />

                <!-- Container -->
                <div class="space-y-0.5">
                    <div v-for="x in taskTemplatesAsOptions"
                         :key="x.id"
                         class="f-x-between">
                        <!-- Description -->
                        <div class="f space-x-0.5">
                            <!-- Index -->
                            <our-label class="no-shrink"
                                       :value="x.index" />

                            <!-- Name -->
                            <our-label :value="x.name"
                                       compact />
                        </div>

                        <!-- Actions -->
                        <div class="f-y-center space-x-0.5">
                            <!-- Status -->
                            <el-tooltip v-if="x.status_at"
                                        :content="x.status_at">
                                <el-tag size="mini"
                                        :type="x.mood">
                                    {{ x.status }}
                                </el-tag>
                            </el-tooltip>

                            <el-tag v-else
                                    size="mini"
                                    :type="x.mood">
                                {{ x.status }}
                            </el-tag>

                            <!-- Download -->
                            <el-tooltip :content="labels.downloadDocument">
                                <el-button icon="el-icon-download"
                                           size="mini"
                                           circle
                                           @click="download(x)" />
                            </el-tooltip>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Analysis -->
            <div v-if="hasAnalysis"
                 class="space-y-1">
                <!-- Title -->
                <our-label :value="labels.analysis"
                           primary />

                <!-- Container -->
                <div class="space-y-0.5">
                    <div v-for="x in taskAnalysisAsOptions"
                         :key="x.id"
                         class="f-x-between space-x-1 f-y-center">
                        <!-- Description -->
                        <div class="f">
                            <!-- Index -->
                            <is-label class="no-shrink w-1.5"
                                      :value="x.index" />

                            <div class="f-col">
                                <document-thumb v-for="doc in x.documents" 
                                                :key="doc.id" 
                                                :document="doc"
                                                size="mini"
                                                with-icon
                                                with-version
                                                can-download
                                                label-class="max-w-20"
                                                label-truncate />
                            </div>
                        </div>
                        
                        <!-- Actions -->
                        <div class="f-y-center space-x-0.5">
                            <!-- Status -->
                            <el-tag v-if="!x.canView"
                                    size="mini"
                                    :type="x.mood">
                                {{ x.status }}
                            </el-tag>

                            <!-- View -->
                            <el-tooltip v-if="x.canView"
                                        :content="labels.view">
                                <el-button icon="el-icon-download"
                                           size="mini" 
                                           circle
                                           @click="openAnalysisResult(x.event)" />
                            </el-tooltip>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Footer -->
            <div class="f-x-end f-y-center">
                <!-- Remove -->
                <access :permissions="deletionPermissions"
                        hidable>
                    <div>
                        <!-- Short -->
                        <el-tooltip v-if="hasTemplates || hasAnalysis"
                                    :content="labels.remove">
                            <el-button icon="el-icon-delete"
                                       type="danger"
                                       size="mini"
                                       circle
                                       @click="remove" />
                        </el-tooltip>

                        <!-- Full -->
                        <el-button v-else-if="!task.is_deleted"
                                   icon="el-icon-delete"
                                   type="danger"
                                   size="mini"
                                   @click="remove">
                            {{ labels.remove }}
                        </el-button>
                    </div>
                </access>
            </div>
        </div>
    </el-popover>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import { actionable, resourceable } from '@/store/connectors'

import { then } from '@/utils/immutable'
import { labelAt } from '@/utils/date'

import DocumentThumb from '@/components/documents/DocumentThumb'

const labels = {
  remove: 'Удалить задачу',
  documents: 'Документы',
  analysis: 'Результаты ИИ-анализа',
  status: 'Не обработано',
  view: 'Загрузить результат анализа',
  downloadDocument: 'Загрузить документ'
}

export default {
  components: {
    DocumentThumb
  },
  mixins: [
    resourceable({ on: 'tasks', name: 'taskAnalysisEvent' }),
    resourceable({ on: 'tasks', name: 'taskAnalysis' }),
    resourceable({ on: 'tasks', name: 'taskTemplates' }),

    actionable({ on: 'tasks', name: 'downloadTaskTemplate', loadable: true })
  ],
  props: {
    task: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      analysis: [],
    }
  },
  computed: {
    ...mapGetters('tasks', [
      'isDefectsType',
      'isBuildingOrderType',
      'isAcceptanceType',
      'isApprovalType'
    ]),

    classes() {
      return {
        content: {
          'space-y-1 border-box _p-0.5 max-h-32 overflow-hidden scroll-y with-custom-scrollbar': true,
          'w-full': !this.hasTemplates && !this.hasAnalysis,
          'w-36': !!this.hasTemplates || this.hasAnalysis, 
        }
      }
    },

    labels() {
      return {
        ...labels
      }
    },

    loading() {
      return this.taskTemplatesLoading || this.downloadTaskTemplateLoading || this.taskAnalysisLoading
    },

    hasTemplates() {
      return !!this.taskTemplates.length
    },

    hasAnalysis() {
      return this.isApprovalType && this.analysis.length
    },

    taskAnalysisAsOptions() {
      return (this.analysis || []).map((x, i) => ({
        id: x.id,
        index: `${i + 1}.`,
        documents: x.project_documents,
        status: x.promise?.status_translated || labels.status,
        mood: x.promise?.status_mood || '',
        progress: x.promise?.progress,
        canView: x.promise?.has_answer,
        progressView: x.promise && x.promise?.status_mood !== 'danger' && x.promise?.status_mood !== 'success',
        event: x,
      }))
    },

    taskTemplatesAsOptions() {
      return (this.taskTemplates || []).map((x, i) => ({
        id: x.id,
        index: `${i + 1}.`,
        name: x['name_translated'],
        status: x['promise_status_translated'] || labels.status,
        status_at: then(x['promise_status_changed_at'], x => labelAt(x, { withCapital: true, iso: true, zoned: false })),
        mood: x['promise_status_mood']
      }))
    },

    deletionPermissions() {
      return [
        this.isDefectsType && 'project_tasks_defects_delete'
          || this.isBuildingOrderType && 'project_tasks_building_orders_delete'
          || this.isAcceptanceType && 'project_tasks_acceptance_works_delete'
          || this.isApprovalType && 'project_tasks_approval_process_delete'
      ]
    }
  },
  beforeDestroy() {
    this.unsubscribe()
  },
  methods: {
    ...mapMutations('dialog', { showDialog: 'SHOW_DIALOG' }),

    fetch() {
      Promise.all([
        this.task && this.isDefectsType && this.fetchTaskTemplates({ task: this.task }),
        this.task && this.isApprovalType && this.fetchTaskAnalysis({ taskId: this.task.id, withProjectDocuments: true, withPromise: true })
          .then((data) => this.analysis = data)
          .then(() => {
            this.subscribe()
          })
      ]).finally(() => {
        this.$nextTick(() => {
          if (this.$refs.popover) {
            this.$refs.popover.updatePopper()
          }
        })
      })
    },

    subscribe() {
      this.$socket.echoClient.private('v1.document-insight-promise').listen('.document-insight-promise.updated', ({ subject }) => {
        const event = this.analysis.find(x => x.promise?.id === subject.id)
        
        if (event) {
          this.fetchTaskAnalysisEvent({ taskId: this.task.id, eventId: event.id, withProjectDocuments: true, withPromise: true })
            .then((data) => {
              this.analysis = this.analysis.map((x) => x.id === data.id ? data : x)
            })
        }
      })
    },

    unsubscribe() {
      this.$socket.echoClient.leave('v1.document-insight-promise')
    },

    download({ id }) {
      this.downloadTaskTemplate({ task: this.task, taskTemplateId: id })
    },

    remove() {
      this.$emit('task-remove', this.task)
    },

    openAnalysisResult(analysisEvent) {
      this.showDialog({
        dialogName: 'approval-ai-analysis',
        payload: {
          analysisEvent,
          task: this.task
        }
      })
    }
  }
}
</script>
